import React, {
  useRef,
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from "react";
import SearchPanel from "@widgets/SearchPanel"; // 조회조건 Panel
import { fnGetDataSet, fnSave } from "@shared/api/restApi"; // BackEnd API
import { getInitFormData } from "@shared/utils/CommonUtils"; // 공통 유틸 메뉴 초기화(조회조건, 그리드, 프리폼) 설정 load 관련 내용
import { showDialog } from "@widgets/CommonDialog"; // Message Box
import { global } from "@shared/libs/Global"; // Login User관련 전역 변수( 로그인 정보)
import SnGrid from "@shared/components/Grid/SnGrid"; // 공통 그리드
import { menuInfo } from "@shared/type/CommonType"; // 공통 Interface
import SnButton from "@shared/components/SnButton";
import SnResizable from "@shared/components/SnResizable";
import usePanelSizes from "@shared/hooks/usePanelSizes";

interface Props {
  Menuprops: menuInfo;
}

interface RowData {
  KeyField?: string; // 행의 고유 ID
  [key: string]: any;
}

const SN_PRD_BAS_REG_001 = forwardRef(({ Menuprops }: Props, ref) => {
  //#region  variable
  const gridApiRefH = useRef<any>(null); // 첫 번째  그리드 Reference 변수
  const gridApiRefL = useRef<any>(null); // 두 번째  그리드 Reference 변수
  const searchRef = useRef<any>(null);
  const today = new Date();
  const todayform = `${today.getFullYear()}${(today.getMonth() + 1)
    .toString()
    .padStart(2, "0")}${today.getDate().toString().padStart(2, "0")}`;
  const { userInfo } = global(); // Login user 정보
  //#endregion

  //#region  Components useState
  const [formData, setFormData] = useState<any>(); // 조회조건 변수
  const [formControlData, setFormControlData] = useState<any[]>([]); // 조회조건 설정된 항목 binding
  const [columns1, setColums1] = useState<any[]>([]); // gridApiRefH 컬럼 설정 관련
  const [columns2, setColums2] = useState<any[]>([]); // gridApiRefL 컬럼 설정 관련
  const [dataSource1, setDataSource1] = useState<any[]>([]); // gridApiRefH datasource 관련
  const [dataSource2, setDataSource2] = useState<any>(); // gridApiRefL datasource 관련
  const [gridFocus, setGridFocus] = useState(false);
  //#endregion

  //#region panel 사이즈 관련
  const cd_menu = Menuprops.cd_menu || "SN_PRD_BAS_REG_001";
  const defaultSize = { panel1width: "49.5%", panel2width: "49.5%" };
  const [sizes, onResizeStop] = usePanelSizes(cd_menu, defaultSize);
  //#endregion

  //#region Initialize
  useEffect(() => {
    const fetchform = async () => {
      setDataSource1([]);
      setDataSource2([]);
      try {
        const defaultForm = {
          cd_biz: userInfo.cd_biz,
          cd_emp: userInfo.cd_emp,
          nm_emp: userInfo.nm_emp,
        };
        const rtn: any = await getInitFormData();
        setFormControlData(rtn.formControlData); // 조회조건 항
        setFormData({ ...rtn.formData, ...defaultForm }); // 조회조건 초기값
        setColums1(rtn.grid.grid1); // H그리드 항목 설정
        setColums2(rtn.grid.grid2);
      } catch (error) {}
    };
    fetchform();
  }, []);

  useEffect(() => {
    gridApiRefH.current.selectAndFocusRow(0);
    setGridFocus(false);
  }, [gridFocus]);

  //#region  Button Event
  useImperativeHandle(ref, () => ({
    onSearch,
    onInsert,
    onDelete,
    onSave,
  }));

  //#region OnSearch
  const onSearch = async () => {
    const modifiedRowsH = gridApiRefH.current.getChange();
    const modifiedRowsL = gridApiRefL.current.getChange();

    if (
      modifiedRowsH.modifiedRowcount > 0 ||
      modifiedRowsL.modifiedRowcount > 0
    ) {
      const confirm = await showDialog(
        "confirm",
        "수정된 내용이 있습니다. 저장하시겠습니까?"
      );
      if (confirm) {
        try {
          gridApiRefH.current.api.stopEditing();
          gridApiRefL.current.api.stopEditing();

          // H 필수값 체크
          const requiredValuesCheckH =
            gridApiRefH.current.requiredValuesCheck();
          if (!requiredValuesCheckH) {
            return;
          }

          //L 필수값 체크
          const requiredValuesCheckL =
            gridApiRefL.current.requiredValuesCheck();
          if (!requiredValuesCheckL) {
            return;
          }
        } catch (e) {}
        await onSave();
      }
    }

    try {
      const params = {
        id: "sn_prd_bas_reg_001_01",
        params: searchRef.current.getFormdata(),
      };

      setDataSource1([]);
      setDataSource2([]);

      const data: any = await fnGetDataSet(params);

      if (data[0] && data[0].length > 0) {
        setDataSource1(data[0]);
        setDataSource2(data[1]);
        await new Promise((resolve) => setTimeout(resolve, 50));
      }
      setGridFocus(true);
      gridApiRefH.current.resetUpdate();
      gridApiRefL.current.resetUpdate();
    } catch (error: any) {
      showDialog("error", error.message);
    }
  };

  //#region On Insert
  const onInsert = async () => {
    try {
      const newRow = {};
      gridApiRefH.current?.addRow(newRow);
      setTimeout(() => {
        gridApiRefH.current.api.setFocusedCell(
          gridApiRefH.current.api.getDisplayedRowCount() - 1,
          "cd_rout"
        );
      }, 50);
    } catch (error: any) {}
  };

  //#region On Delete
  const onDelete = async () => {
    const gridApiH = gridApiRefH.current.api;
    const rowCount = gridApiH.getDisplayedRowCount();
    if (rowCount === 0) {
      return;
    }
    const confirm = await showDialog("confirm", "삭제하시겠습니까?");
    if (!confirm) {
      return;
    }
    gridApiRefL.current?.deleteAllRows();
    gridApiRefH.current?.deleteSelectedRows();

    const realDeletedRows = gridApiRefH.current.getChange().delete ?? [];
    if (Array.isArray(realDeletedRows) && realDeletedRows.length < 1) {
      gridApiRefH.current.resetUpdate();
      gridApiRefL.current.resetUpdate();
      return;
    }
    await onSave();
  };

  //#region OnAddRow
  const onAddRow = async () => {
    try {
      const newRow = {};
      gridApiRefL.current?.addRow(newRow);
      setTimeout(() => {
        const selectedNode = gridApiRefL.current.api.getSelectedNodes()[0];
        onCellValueChanged(selectedNode, "");
        const renderedNodes = gridApiRefL.current.api.getRenderedNodes();
        for (const node of renderedNodes) {
          const ynEnd = "Y";
          if (node.isSelected()) {
            gridApiRefL.current.setValue(node, "yn_end", "Y");
            return;
          }
          gridApiRefL.current.setValue(node, "yn_end", !ynEnd);
        }
      }, 100);
    } catch (error: any) {}
  };

  //#region OnDeleteRow
  const onDeleteRow = async () => {
    try {
      const api = gridApiRefL.current?.api;
      if (!api) return;

      const focusedCell = api.getFocusedCell();
      const deleteIndex = focusedCell?.rowIndex ?? -1;
      const selectedNodeBeforeDelete = api.getSelectedNodes();
      let ynEndValue = selectedNodeBeforeDelete[0].data.yn_end;
      gridApiRefL.current?.deleteSelectedRows();
      await new Promise((resolve) => setTimeout(resolve, 100));
      const rowCount = api.getDisplayedRowCount();
      if (ynEndValue !== "Y") return;
      let nextFocusIndex = deleteIndex;
      if (nextFocusIndex >= rowCount) {
        nextFocusIndex = rowCount - 1;
      }
      const nextRowNode = api.getDisplayedRowAtIndex(nextFocusIndex);
      if (!nextRowNode) return;

      if (nextRowNode.data?.yn_full === "N") {
        if (rowCount === 1) return;
        const previousNode = api.getDisplayedRowAtIndex(nextFocusIndex - 1);
        if (previousNode?.data?.yn_full === "Y") {
          previousNode.data.yn_end = "Y";
          api.applyTransaction({ update: [previousNode.data] });
        }
      } else {
        nextRowNode.data.yn_end = "Y";
        api.applyTransaction({ update: [nextRowNode.data] });
      }
    } catch (error: any) {}
  };

  //#region BeforeAddH
  const onBeforeAddRowH = (newRow: RowData): boolean => {
    try {
      const prdRoutChanges = gridApiRefH.current.getChange().insert;
      if (prdRoutChanges.length > 0) return false;
      const selectedFormData = searchRef.current.getFormdata();

      const updatedRow = {
        cd_firm: userInfo.cd_firm,
        cd_biz: selectedFormData.cd_biz,
        yn_use: "Y",
        yn_wobill: "N",
        tp_rout: "S",
      };
      Object.assign(newRow, updatedRow);
      return true;
    } catch (error: any) {
      return false;
    }
  };

  //#region BeforeAddL
  const onBeforeAddRowL = (newRow: RowData): boolean => {
    try {
      if (
        !gridApiRefH ||
        gridApiRefH.current.api.getSelectedNodes().length === 0
      ) {
        alert("선택된 행이 없습니다.");
        return false;
      }
      const requiredValuesCheckH = gridApiRefH.current.requiredValuesCheck();
      if (!requiredValuesCheckH) {
        return false;
      }

      const selectedRows = gridApiRefH.current.api.getSelectedNodes();
      const gridApiL = gridApiRefL.current.api;
      const rowCount = gridApiL ? gridApiL.getDisplayedRowCount() : 0;
      const selectedCdRout = selectedRows[0]?.data.cd_rout;

      const allRowData: any[] = [];
      gridApiRefL.current.api.forEachNodeAfterFilterAndSort((node: any) => {
        allRowData.push(node.data);
      });

      const maxNoLine = allRowData
        .filter((row: any) => row.cd_rout === selectedCdRout)
        .map((row: any) => row.no_seq || 0)
        .reduce((max, current) => Math.max(max, current), 0);
      const updatedRow = {
        cd_firm: userInfo.cd_firm,
        cd_biz: userInfo.cd_biz,
        cd_rout: selectedCdRout,
        fg_op: "OP1",
        no_seq: maxNoLine + 1,
        yn_full: "Y",
        fg_machine: "PC",
        yn_end: "N",
        nm_op: "N",
        yn_sct: "N",
      };

      Object.assign(newRow, updatedRow);
      gridApiRefL.current.applyFilters({
        cd_rout: selectedCdRout,
      });
      return true;
    } catch (error: any) {
      return false;
    }
  };

  //#region  OnSave
  const onSave = async () => {
    let prdRoutChanges;
    let prdRoutopChanges;

    try {
      gridApiRefH.current.api.stopEditing();
      gridApiRefL.current.api.stopEditing();
      // H 필수값 체크
      const requiredValuesCheckH = gridApiRefH.current.requiredValuesCheck();
      if (!requiredValuesCheckH) {
        return;
      }

      //L 필수값 체크
      const requiredValuesCheckL = gridApiRefL.current.requiredValuesCheck();
      if (!requiredValuesCheckL) {
        return;
      }

      prdRoutChanges = gridApiRefH.current.getChange();
      prdRoutopChanges = gridApiRefL.current.getChange();
      console.log(prdRoutopChanges);
      if (
        prdRoutChanges.modifiedRowcount === 0 &&
        prdRoutopChanges.modifiedRowcount === 0
      ) {
        await showDialog("alter", "변경된 내용이 없습니다.");
        return;
      }
      const SaveData = {
        prd_rout: prdRoutChanges,
        prd_routop: prdRoutopChanges,
      };
      await fnSave("SN_PRD_BAS_REG_001_SAVE_01", SaveData);
      gridApiRefH.current.resetUpdate();
      gridApiRefL.current.resetUpdate();
    } catch (error: any) {}
  };
  //#endregion

  //#region Filter
  const onFocusedRowChanged = (event: any) => {
    const selectedRow = event;
    if (selectedRow) {
      const cd_rout = selectedRow.cd_rout;
      gridApiRefL.current.applyFilters({ cd_rout: cd_rout });
      gridApiRefL.current.selectAndFocusRow(0);
    }
  };

  //region CellValueChanged
  const onCellValueChanged = async (selectedNode: any, colId: any) => {
    if (!selectedNode) return;
    const data = selectedNode.data;
    if (colId === "yn_end") {
      const parentNode = gridApiRefH.current.api.getSelectedNodes()[0];
      if (!parentNode) return;

      gridApiRefL.current.api.forEachNode((node: any) => {
        if (node.data.cd_rout !== parentNode.data.cd_rout) return;

        const newValue = node === selectedNode ? "Y" : "N";

        if (node.data.yn_end !== newValue) {
          gridApiRefL.current.setValue(node, "yn_end", !node.data.yn_end);
          node.data.yn_end = newValue;
        }
      });
    } else if (colId === "cd_cust") {
      gridApiRefL.current.api.forEachNodeAfterFilterAndSort((node: any) => {
        console.log(node.data.cd_cust);
        gridApiRefL.current.setValue(node, "cd_cust_sct", node.data.cd_cust);
      });
    } else if (colId === "cd_op") {
      const params = {
        id: "sn_prd_bas_reg_001_02",
        params: {
          cd_firm: userInfo.cd_firm,
          cd_op: selectedNode.data?.cd_op,
        },
      };

      const result: any = await fnGetDataSet(params);
      if (result[0] && result[0].length > 0) {
        const data = result[0][0];
        gridApiRefL.current.setValue(selectedNode, "nm_op", data.nm_op);
        gridApiRefL.current.setValue(selectedNode, "fg_op", data.fg_op);
        gridApiRefL.current.setValue(selectedNode, "yn_full", data.yn_full);
        gridApiRefL.current.setValue(
          selectedNode,
          "fg_machine",
          data.fg_machine
        );
      }
    } else if (colId === "yn_sct") {
      if (data.yn_sct === "N") {
        if (data.cd_cust) {
          gridApiRefL.current.setValue(selectedNode, "cd_cust", "");
          gridApiRefL.current.setValue(selectedNode, "nm_cust_sort", "");
        }
      }
    }
  };

  //#region onBtnClick
  const moveRow = (direction: "up" | "down") => {
    const gridApi = gridApiRefL.current.api;
    const selectedNodes = gridApi.getSelectedNodes();
    if (selectedNodes.length === 0) return;

    const selectedNode = selectedNodes[0];
    const allNodes = gridApiRefL.current.api.getRenderedNodes();
    const currentIndex = allNodes.findIndex(
      (node: any) => node.id === selectedNode.id
    );

    if (currentIndex < 0) return;
    const targetIndex =
      direction === "up" ? currentIndex - 1 : currentIndex + 1;
    if (targetIndex < 0 || targetIndex >= allNodes.length) return;

    const targetNode = allNodes[targetIndex];

    if (targetNode) {
      swapGridNodes(selectedNode, targetNode, gridApi);
      setTimeout(() => {
        const node = gridApiRefL.current.api.getRowNode(targetNode.id);
        if (node && node.rowIndex != null) {
          gridApiRefL.current.api.setFocusedCell(node.rowIndex, "cd_op");
        } else {
          gridApiRefL.current.api.setFocusedCell(targetIndex, "cd_op");
        }
      }, 0);
    }
  };

  const swapGridNodes = (nodeA: any, nodeB: any, gridApi: any) => {
    if (!nodeA || !nodeB) return;

    const allCols = gridApi
      .getColumnDefs()
      .map((col: any) => col.field)
      .filter((field: any) => !["no_seq"].includes(field));

    allCols.forEach((field: string) => {
      if (!field) return;
      const temp = nodeA.data[field];

      // 체크박스 예외 동작
      if (["yn_sct", "yn_end"].includes(field)) {
        nodeA.data[field] = nodeB.data[field];
        nodeB.data[field] = temp;
        return;
      }

      nodeA.setDataValue(field, nodeB.data[field]);
      nodeB.setDataValue(field, temp);
    });

    gridApi.refreshCells({
      rowNodes: [nodeA, nodeB],
      force: true,
    });
  };

  const onBtnClick = async (id: string) => {
    if (id === "btnRowUp") {
      moveRow("up");
    } else if (id === "btnRowDown") {
      moveRow("down");
    }
  };
  //#endregion

  //#region  UI
  return (
    <div className="h-full flex flex-col">
      <div className="mb-1">
        <SearchPanel
          ref={searchRef}
          data={formData}
          controls={formControlData}
          
        ></SearchPanel>
      </div>
      <div className="flex w-full h-full min-w-0 space-x-1.5">
        <SnResizable
          direction={"width"}
          seq={1}
          cd_menu={cd_menu}
          defaultSize={defaultSize}
          size={sizes.panel1width}
          onResizeStop={onResizeStop}
          enable={{ right: true }}
          minWidth={130}
        >
          <SnGrid
             // Pros로 받은 Menuprops
            ref={gridApiRefH}
            seq={1}
            enableAddRow={true}
            columnConfig={columns1}
            isEditable={true}
            rowData={dataSource1}
            onBeforeAddRow={onBeforeAddRowH}
            onFocusedRowChanged={onFocusedRowChanged}
            rowSelection="single"
            title="공정경로정보"
            resizeController={{
              defaultSize: defaultSize,
              onResizeStop: onResizeStop,
            }}
          />
        </SnResizable>
        <SnResizable
          direction={"width"}
          seq={2}
          cd_menu={cd_menu}
          defaultSize={defaultSize}
          size={sizes.panel2width}
          onResizeStop={onResizeStop}
          enable={{ right: true }}
          minWidth={130}
        >
          <SnGrid
             // Pros로 받은 Menuprops
            ref={gridApiRefL}
            seq={2}
            enableAddRow={true}
            isEditable={true}
            onBtnAddClick={onAddRow}
            onBtnDeleteClick={onDeleteRow}
            onBeforeAddRow={onBeforeAddRowL}
            onCellValueChanged={(e) => {
              setTimeout(() => {
                const selectedNode =
                  gridApiRefL.current.api.getSelectedNodes()[0];
                onCellValueChanged(selectedNode, e.column.getColId());
              }, 100);
            }}
            columnConfig={columns2}
            rowData={dataSource2}
            rowSelection="single"
            title="공정정보"
            header={
              <div>
                <SnButton
                  text={"up"}
                  onClick={() => {
                    onBtnClick("btnRowUp");
                  }}
                />
                <SnButton
                  text={"down"}
                  onClick={() => {
                    onBtnClick("btnRowDown");
                  }}
                />
              </div>
            }
            resizeController={{
              defaultSize: defaultSize,
              onResizeStop: onResizeStop,
            }}
          />
        </SnResizable>
      </div>
    </div>
  );
});

export default SN_PRD_BAS_REG_001;
